import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { SettingsLayout } from "@/components/SettingsLayout";
import { Toaster } from "@/components/ui/sonner";
import { canAccessModule, hasRole, isDeveloper } from "@/lib/permissions";
import Login from "@/pages/Login";
import SharePage from "@/pages/SharePage";
import POInput from "@/pages/POInput";
import AuthCallback from "@/pages/AuthCallback";
const SettingsPreview = lazy(() => import("@/pages/SettingsPreview"));
const MenuPreview = lazy(() => import("@/pages/MenuPreview"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const SeoOptimiser = lazy(() => import("@/pages/SeoOptimiser"));
const FaqCreator = lazy(() => import("@/pages/FaqCreator"));
const SocialStudio = lazy(() => import("@/pages/SocialStudio"));
const BrandGuardian = lazy(() => import("@/pages/BrandGuardian"));
const UsersAccess = lazy(() => import("@/pages/settings/UsersAccess"));
const RolesReference = lazy(() => import("@/pages/settings/RolesReference"));
const AccessSecurity = lazy(() => import("@/pages/settings/AccessSecurity"));
const Integrations = lazy(() => import("@/pages/settings/Integrations"));
const IntegrationDetail = lazy(() => import("@/pages/settings/IntegrationDetail"));
const DeliveryLog = lazy(() => import("@/pages/settings/DeliveryLog"));
const SitesAdmin = lazy(() => import("@/pages/settings/SitesAdmin"));
const ModuleSettings = lazy(() => import("@/pages/settings/ModuleSettings"));
const ModuleSettingsDetail = lazy(() => import("@/pages/settings/ModuleSettingsDetail"));
const MenuSettings = lazy(() => import("@/pages/settings/MenuSettings"));
const ModuleLibrary = lazy(() => import("@/pages/settings/ModuleLibrary"));
const Orchestrator = lazy(() => import("@/pages/settings/Orchestrator"));
const AuditLog = lazy(() => import("@/pages/settings/AuditLog"));
const NotesAdmin = lazy(() => import("@/pages/settings/NotesAdmin"));
const DataSync = lazy(() => import("@/pages/settings/DataSync"));
const SystemStatus = lazy(() => import("@/pages/settings/SystemStatus"));
const ThemeDesign = lazy(() => import("@/pages/ThemeDesign"));
const SeoMetaEditor = lazy(() => import("@/pages/SeoMetaEditor"));
const Activity = lazy(() => import("@/pages/Activity"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
const AssetLibrary = lazy(() => import("@/pages/AssetLibrary"));
const MyNotes = lazy(() => import("@/pages/MyNotes"));
const AssetGallery = lazy(() => import("@/pages/AssetGallery"));
const MarketingProducts = lazy(() => import("@/pages/MarketingProducts"));
const SuppliersQuotes = lazy(() => import("@/pages/SuppliersQuotes"));
const RfqDetail = lazy(() => import("@/pages/RfqDetail"));
const MarketingRequests = lazy(() => import("@/pages/MarketingRequests"));
const RequestDetail = lazy(() => import("@/pages/RequestDetail"));
const Sponsorship = lazy(() => import("@/pages/Sponsorship"));
const SponsorshipDetail = lazy(() => import("@/pages/SponsorshipDetail"));
const BrowseByTag = lazy(() => import("@/pages/BrowseByTag"));
const GroupLeads = lazy(() => import("@/pages/GroupLeads"));
const CampaignSignals = lazy(() => import("@/pages/CampaignSignals"));
const GroupLeadsSpamRules = lazy(() => import("@/pages/GroupLeadsSpamRules"));
const PaymentsRegister = lazy(() => import("@/pages/PaymentsRegister"));
const WebsiteManager = lazy(() => import("@/pages/WebsiteManager"));
const WebsiteManagerDetail = lazy(() => import("@/pages/WebsiteManagerDetail"));
import { HelpProvider } from "@/context/HelpContext";
import { HelpDrawer } from "@/components/help/HelpDrawer";
import { CommandPalette } from "@/components/help/CommandPalette";
import { UnsavedChangesProvider } from "@/context/UnsavedChangesContext";
// SCAFFOLD_IMPORTS
const Profile = lazy(() => import("@/pages/settings/Profile"));
import { ThemeProvider } from "@/context/ThemeContext";

const Spinner = () => (
  <div className="h-screen flex items-center justify-center bg-zinc-950">
    <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
  </div>
);

function Protected({ children, module, requireRole, allowRoles }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (requireRole && !hasRole(user, requireRole)) return <Navigate to="/" replace />;
  const roleAllowed = allowRoles && allowRoles.includes(user.role);
  if (module && !roleAllowed && !canAccessModule(user, module)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AdminSettings({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasRole(user, "admin")) return <Navigate to="/" replace />;
  return <SettingsLayout>{children}</SettingsLayout>;
}

function ManagerSettings({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasRole(user, "manager")) return <Navigate to="/" replace />;
  return <SettingsLayout>{children}</SettingsLayout>;
}

function DeveloperSettings({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isDeveloper(user)) return <Navigate to="/" replace />;
  return <SettingsLayout>{children}</SettingsLayout>;
}

function SettingsIndex() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (hasRole(user, "admin")) return <Navigate to="/settings/users" replace />;
  if (hasRole(user, "manager")) return <Navigate to="/settings/module-library" replace />;
  return <Navigate to="/" replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <UnsavedChangesProvider>
    <HelpProvider>
    <Suspense fallback={<Spinner />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/share/:shareId" element={<SharePage />} />
      <Route path="/po/:token" element={<POInput />} />
      <Route path="/settings-preview" element={<SettingsPreview />} />
      <Route path="/menu-preview" element={<MenuPreview />} />

      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/activity" element={<Protected><Activity /></Protected>} />
      <Route path="/browse" element={<Protected><BrowseByTag /></Protected>} />
      <Route path="/seo" element={<Protected module="seo"><SeoOptimiser /></Protected>} />
      <Route path="/faq" element={<Protected module="faq"><FaqCreator /></Protected>} />
      <Route path="/social" element={<Protected module="social"><SocialStudio /></Protected>} />
      <Route path="/brand-guardian" element={<Protected module="brand_guardian"><BrandGuardian /></Protected>} />
      <Route path="/theme-design" element={<Protected module="theme_design" requireRole="admin"><ThemeDesign /></Protected>} />
      <Route path="/seo-meta" element={<Protected module="seo_meta"><SeoMetaEditor /></Protected>} />
      <Route path="/asset-library" element={<Protected module="assetlibrary"><AssetLibrary /></Protected>} />
      <Route path="/asset-library/gallery" element={<Protected module="assetlibrary"><AssetGallery /></Protected>} />
      <Route path="/products" element={<Protected module="products"><MarketingProducts /></Protected>} />
      <Route path="/suppliers-quotes" element={<Protected module="suppliersquotes" allowRoles={["purchasing"]}><SuppliersQuotes /></Protected>} />
      <Route path="/suppliers-quotes/:rfqId" element={<Protected module="suppliersquotes" allowRoles={["purchasing"]}><RfqDetail /></Protected>} />
      <Route path="/requests" element={<Protected module="requests"><MarketingRequests /></Protected>} />
      <Route path="/requests/:id" element={<Protected module="requests"><RequestDetail /></Protected>} />
      <Route path="/sponsorship" element={<Protected module="sponsorship"><Sponsorship /></Protected>} />
      <Route path="/sponsorship/:id" element={<Protected module="sponsorship"><SponsorshipDetail /></Protected>} />
      <Route path="/group-leads" element={<Protected module="group_leads"><GroupLeads /></Protected>} />
      <Route path="/campaign-signals" element={<Protected module="campaign_signals" requireRole="manager"><CampaignSignals /></Protected>} />
      <Route path="/group-leads/spam-rules" element={<Protected module="group_leads" requireRole="admin"><GroupLeadsSpamRules /></Protected>} />
      <Route path="/payments" element={<Protected module="payments"><PaymentsRegister /></Protected>} />
      <Route path="/website-manager" element={<Protected module="website_manager"><WebsiteManager /></Protected>} />
      <Route path="/website-manager/:id" element={<Protected module="website_manager"><WebsiteManagerDetail /></Protected>} />
      <Route path="/orchestrator" element={<Protected module="orchestrator"><Orchestrator /></Protected>} />
      <Route path="/help" element={<Protected><HelpCenter /></Protected>} />
      <Route path="/my-notes" element={<Protected><MyNotes /></Protected>} />
      <Route path="/help/:slug" element={<Protected><HelpCenter /></Protected>} />
      <Route path="/pages" element={<Navigate to="/seo" replace />} />
      {/* SCAFFOLD_ROUTES */}

      <Route path="/settings" element={<SettingsIndex />} />
      <Route path="/settings/users" element={<AdminSettings><UsersAccess /></AdminSettings>} />
      <Route path="/settings/roles" element={<AdminSettings><RolesReference /></AdminSettings>} />
      <Route path="/settings/sites" element={<AdminSettings><SitesAdmin /></AdminSettings>} />
      <Route path="/settings/security" element={<AdminSettings><AccessSecurity /></AdminSettings>} />
      <Route path="/settings/integrations" element={<AdminSettings><Integrations /></AdminSettings>} />
      <Route path="/settings/integrations/postmark/log" element={<AdminSettings><DeliveryLog /></AdminSettings>} />
      <Route path="/settings/integrations/:key" element={<AdminSettings><IntegrationDetail /></AdminSettings>} />
      <Route path="/settings/modules" element={<AdminSettings><ModuleSettings /></AdminSettings>} />
      <Route path="/settings/modules/:key" element={<AdminSettings><ModuleSettingsDetail /></AdminSettings>} />
      <Route path="/settings/menu" element={<AdminSettings><MenuSettings /></AdminSettings>} />
      <Route path="/settings/audit" element={<AdminSettings><AuditLog /></AdminSettings>} />
      <Route path="/settings/notes" element={<AdminSettings><NotesAdmin /></AdminSettings>} />
      <Route path="/settings/campaign-signals" element={<Navigate to="/settings/modules/campaign-signals" replace />} />
      <Route path="/settings/data-sync" element={<AdminSettings><DataSync /></AdminSettings>} />
      <Route path="/settings/system" element={<AdminSettings><SystemStatus /></AdminSettings>} />
      <Route path="/settings/module-library" element={<ManagerSettings><ModuleLibrary /></ManagerSettings>} />
      <Route path="/settings/orchestrator" element={<DeveloperSettings><Orchestrator /></DeveloperSettings>} />
      <Route path="/settings/blueprints" element={<Navigate to="/settings/orchestrator" replace />} />
      <Route path="/settings/profile" element={<ManagerSettings><Profile /></ManagerSettings>} />
    </Routes>
    </Suspense>
    <HelpDrawer />
    <CommandPalette />
    </HelpProvider>
    </UnsavedChangesProvider>
  );
}

function App() {
  useEffect(() => {
    const mode = localStorage.getItem("mh_theme_mode") || "dark";
    document.documentElement.classList.toggle("dark", mode !== "light");
    document.documentElement.classList.toggle("light", mode === "light");
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" />
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
